import { Center, Collapse, Flex, Spinner, Text, useDisclosure } from '@chakra-ui/react';

import { Transaction } from '@aslanpay/portal-common-lib';

import CurrencyAmount from 'app/components/UI/CurrencyAmount/CurrencyAmount';
import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import {
  TransactionAccountLabels,
  TransactionCategoryLabels,
  TransactionStatusLabels,
} from 'app/constants/transactions';

import useFetchChildTransactions from 'app/hooks/useFetchChildTransactions';

import { format, isToday, isYesterday, parseISO } from 'date-fns';

interface TransactionDetailsProps {
  transaction?: Transaction;
}

function TransactionDetails({ transaction = undefined }: TransactionDetailsProps) {
  const { data: childTransactions, isLoading: isLoadingChildTransactions } =
    useFetchChildTransactions(transaction?.id || '');

  const { isOpen, onToggle } = useDisclosure();

  if (!transaction) {
    return <Text textStyle="extraSmallMedium">Click a transaction to see details</Text>;
  }

  const formatDate = (dateString: string) => {
    const date = parseISO(dateString);
    const time = format(date, 'HH:mm');
    if (isToday(date)) {
      return `Today · ${time}`;
    }
    if (isYesterday(date)) {
      return `Yesterday · ${time}`;
    }
    return `${format(date, 'MMM dd')} · ${time}`;
  };

  const getStatusColor = () => {
    if (transaction.transactionStatus === 'SETTLED') return { bg: 'green.50', text: 'green.600' };
    if (transaction.transactionStatus === 'PENDING') return { bg: 'yellow.50', text: 'yellow.600' };
    return { bg: 'red.50', text: 'red.600' };
  };

  const statusColor = getStatusColor();

  return (
    <Flex flexDir="column" gap="16px">
      {/* A) Primary Summary Block */}
      <Flex
        flexDir="column"
        padding="16px"
        bgColor="brand.lightGray2"
        borderRadius="10px"
        border="1px solid"
        borderColor="brand.lightGray1"
        gap="8px"
      >
        <CurrencyAmount
          amount={transaction.totalAmount}
          currency={transaction.currency}
          direction={transaction.transactionDirection}
          showSign
          textStyle="h4"
          fontWeight="bold"
        />
        <Text textStyle="smallMedium">
          {TransactionCategoryLabels[transaction.transactionCategory] ||
            transaction.transactionCategory}
        </Text>
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          {formatDate(transaction.createdAt)}
        </Text>
        <Flex px="8px" py="4px" bgColor={statusColor.bg} borderRadius="6px" alignSelf="flex-start">
          <Text textStyle="extraSmallBold" color={statusColor.text}>
            {TransactionStatusLabels[transaction.transactionStatus]}
          </Text>
        </Flex>
      </Flex>

      {/* B) Payment Breakdown */}
      <Flex
        flexDir="column"
        padding="16px"
        bgColor="brand.lightGray2"
        borderRadius="10px"
        border="1px solid"
        borderColor="brand.lightGray1"
        gap="12px"
      >
        <Text textStyle="smallBold">Payment Breakdown</Text>
        <Flex flexDir="column" gap="8px">
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Amount
            </Text>
            <CurrencyAmount
              amount={transaction.amount}
              currency={transaction.currency}
              textStyle="extraSmallMedium"
            />
          </Flex>
          <Flex justify="space-between">
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              FX fee
            </Text>
            <CurrencyAmount
              amount={transaction.fxFee || 0}
              currency={transaction.currency}
              textStyle="extraSmallMedium"
            />
          </Flex>
          {transaction.originalAmount && (
            <>
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Original amount
                </Text>
                <CurrencyAmount
                  amount={transaction.originalAmount}
                  currency={transaction.originalCurrency}
                  textStyle="extraSmallMedium"
                />
              </Flex>
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Conversion rate
                </Text>
                <Text textStyle="extraSmallMedium" fontFamily="Inconsolata, monospace">
                  {transaction.conversionRate?.toFixed(6)}
                </Text>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>

      {/* C) Meta Info - Collapsible */}
      <Flex
        flexDir="column"
        padding="16px"
        bgColor="brand.lightGray2"
        borderRadius="10px"
        border="1px solid"
        borderColor="brand.lightGray1"
        gap="12px"
      >
        <Flex
          justify="space-between"
          align="center"
          cursor="pointer"
          onClick={onToggle}
          _hover={{ opacity: 0.7 }}
        >
          <Text textStyle="smallBold">More details</Text>
          <Text textStyle="extraSmallRegular">{isOpen ? '▲' : '▼'}</Text>
        </Flex>
        <Collapse in={isOpen} animateOpacity>
          <Flex flexDir="column" gap="8px">
            <Flex justify="space-between">
              <Text textStyle="extraSmallRegular" color="brand.darkGray">
                Transaction ID
              </Text>
              <TextWithClipboard value={transaction.id} />
            </Flex>
            {transaction.externalId && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  External ID
                </Text>
                <TextWithClipboard value={transaction.externalId} />
              </Flex>
            )}
            {transaction.externalCardId && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  External card ID
                </Text>
                <TextWithClipboard value={transaction.externalCardId} />
              </Flex>
            )}
            {transaction.externalCardTokenId && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  External card token ID
                </Text>
                <TextWithClipboard value={transaction.externalCardTokenId} />
              </Flex>
            )}
            {transaction.payCycleId && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Pay cycle ID
                </Text>
                <TextWithClipboard value={transaction.payCycleId} />
              </Flex>
            )}
            {transaction.entryModeType && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Entry mode
                </Text>
                <Text textStyle="extraSmallMedium">{transaction.entryModeType}</Text>
              </Flex>
            )}
            {transaction.posEntryMode && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  POS entry mode
                </Text>
                <Text textStyle="extraSmallMedium">{transaction.posEntryMode}</Text>
              </Flex>
            )}
            {transaction.mcc && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  MCC
                </Text>
                <Text textStyle="extraSmallMedium">{transaction.mcc}</Text>
              </Flex>
            )}
            <Flex justify="space-between">
              <Text textStyle="extraSmallRegular" color="brand.darkGray">
                Merchant
              </Text>
              <Text textStyle="extraSmallMedium">{transaction.otherMerchant || '–'}</Text>
            </Flex>
            {transaction.notes && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Notes
                </Text>
                <Text textStyle="extraSmallMedium" maxW="200px" textAlign="right">
                  {transaction.notes}
                </Text>
              </Flex>
            )}
            {transaction.transactionReason && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Reason
                </Text>
                <Text textStyle="extraSmallMedium" maxW="200px" textAlign="right">
                  {transaction.transactionReason}
                </Text>
              </Flex>
            )}
            {transaction.transactionType && (
              <Flex justify="space-between">
                <Text textStyle="extraSmallRegular" color="brand.darkGray">
                  Type
                </Text>
                <Text textStyle="extraSmallMedium">{transaction.transactionType}</Text>
              </Flex>
            )}
          </Flex>
        </Collapse>
      </Flex>

      {/* Child Transactions */}
      <Flex
        flexDir="column"
        padding="16px"
        bgColor="brand.lightGray2"
        borderRadius="10px"
        border="1px solid"
        borderColor="brand.lightGray1"
        gap="12px"
      >
        <Text textStyle="smallBold">Child transactions</Text>
        {isLoadingChildTransactions && !childTransactions && (
          <Center width="full" py="20px">
            <Spinner color="brand.purple" size="md" />
          </Center>
        )}
        {!isLoadingChildTransactions && childTransactions && childTransactions.length > 0 && (
          <Flex flexDir="column" gap="8px">
            {childTransactions.map((childTransaction) => (
              <Flex
                key={childTransaction.id}
                flexDir="column"
                gap="4px"
                p="12px"
                bgColor="white"
                borderRadius="6px"
                border="1px solid"
                borderColor="brand.lightGray1"
              >
                <Flex justify="space-between">
                  <Text textStyle="extraSmallBold">
                    {TransactionAccountLabels[childTransaction.transactionAccount]}
                  </Text>
                  <CurrencyAmount
                    amount={childTransaction.amount}
                    currency={transaction.currency}
                    textStyle="extraSmallMedium"
                  />
                </Flex>
                {childTransaction.grossAmount && (
                  <Flex justify="space-between">
                    <Text textStyle="extraSmallRegular" color="brand.darkGray">
                      Gross amount
                    </Text>
                    <CurrencyAmount
                      amount={childTransaction.grossAmount}
                      currency={transaction.currency}
                      textStyle="extraSmallMedium"
                    />
                  </Flex>
                )}
              </Flex>
            ))}
          </Flex>
        )}
        {!isLoadingChildTransactions && (!childTransactions || childTransactions.length === 0) && (
          <Text textStyle="extraSmallRegular" color="brand.darkGray">
            No child transactions
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

export default TransactionDetails;
